import api from './api';
import { Project, ProjectStatus, JobType, WorkMode, Visibility } from '../types/Project';

export interface ProjectListResponse {
  success: boolean;
  count: number;
  pagination: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
  data: Project[];
}

export interface ProjectResponse {
  success: boolean;
  data: Project;
}

export interface ProjectQueryParams {
  page?: number;
  limit?: number;
  status?: ProjectStatus;
  search?: string;
  job_type?: JobType;
  work_mode?: WorkMode;
  visibility?: Visibility;
  is_featured?: boolean;
}

class ProjectService {
  /**
   * Get company projects
   */
  async getCompanyProjects(companyId: string, params: ProjectQueryParams = {}): Promise<ProjectListResponse> {
    try {
      const response = await api.get<ProjectListResponse>(`/companies/${companyId}/projects`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching company projects:', error);
      throw error;
    }
  }

  /**
   * Get project by ID
   */
  async getProject(companyId: string, projectId: string): Promise<Project> {
    try {
      const response = await api.get<ProjectResponse>(`/companies/${companyId}/projects/${projectId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching project:', error);
      throw error;
    }
  }

  /**
   * Create project
   */
  async createProject(companyId: string, data: Partial<Project>): Promise<Project> {
    try {
      const response = await api.post<ProjectResponse>(`/companies/${companyId}/projects`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  }

  /**
   * Update project
   */
  async updateProject(companyId: string, projectId: string, data: Partial<Project>): Promise<Project> {
    try {
      const response = await api.put<ProjectResponse>(`/companies/${companyId}/projects/${projectId}`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  }

  /**
   * Update project status
   */
  async updateProjectStatus(companyId: string, projectId: string, status: ProjectStatus): Promise<Project> {
    try {
      const response = await api.patch<ProjectResponse>(
        `/companies/${companyId}/projects/${projectId}/status`, 
        { status }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error updating project status:', error);
      throw error;
    }
  }

  /**
   * Delete project
   */
  async deleteProject(companyId: string, projectId: string): Promise<void> {
    try {
      await api.delete(`/companies/${companyId}/projects/${projectId}`);
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  }

  /**
   * Get project applications
   */
  async getProjectApplications(
    companyId: string, 
    projectId: string, 
    params: { page?: number; limit?: number; status?: string } = {}
  ): Promise<any> {
    try {
      const response = await api.get(
        `/companies/${companyId}/projects/${projectId}/applications`, 
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching project applications:', error);
      throw error;
    }
  }

  /**
   * Increment project view count
   */
  async incrementViewCount(companyId: string, projectId: string): Promise<void> {
    try {
      await api.post(`/companies/${companyId}/projects/${projectId}/views`);
    } catch (error) {
      console.error('Error incrementing project view count:', error);
      // Don't throw error as this is non-critical
    }
  }

  /**
   * Increment project click count
   */
  async incrementClickCount(companyId: string, projectId: string): Promise<void> {
    try {
      await api.post(`/companies/${companyId}/projects/${projectId}/clicks`);
    } catch (error) {
      console.error('Error incrementing project click count:', error);
      // Don't throw error as this is non-critical
    }
  }
}

export default new ProjectService();